import {
  Box,
  Grid,
  List,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import ViolationListItem from 'components/lists/listItems/ViolationListItem';
import { useTranslation } from 'react-i18next';
import { Violation } from 'types';
import FormFrame from 'components/layout/FormFrame';
import DetailsHeader from 'components/layout/DetailsHeader';
import { ValidatorForm } from 'react-material-ui-form-validator';
import AuturaTextField from 'components/inputs/AuturaTextField';
export interface MobileViolationsProps {
  activeViolations: Violation[];
  inactiveViolations: Violation[];
  allowUpdate: boolean;
  allowCreate: boolean;
  name: string;
  description: string;
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  handleCreateViolation: () => void;
  handleIncludeViolation: (item: Violation) => void;
  handleExcludeViolation: (item: Violation) => void;
  handleDeleteViolation: (item: Violation) => void;
}

const MobileViolations = ({
  activeViolations,
  inactiveViolations,
  allowUpdate,
  allowCreate,
  name,
  description,
  setName,
  setDescription,
  handleCreateViolation,
  handleIncludeViolation,
  handleExcludeViolation,
  handleDeleteViolation,
}: MobileViolationsProps): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints, palette } = useTheme();
  const isPhone = useMediaQuery(breakpoints.down('sm'));
  const [viewMode, setViewMode] = useState<'active' | 'inactive' | 'create'>(
    'active',
  );

  const handleClearForm = () => {
    setName('');
    setDescription('');
  };

  const violationsList = useMemo(() => {
    if (viewMode === 'active') {
      return activeViolations;
    } else {
      return inactiveViolations;
    }
  }, [activeViolations, inactiveViolations, viewMode]);

  const handleCreateClick = () => {
    handleCreateViolation();
    setViewMode('active');
  };

  return (
    <ValidatorForm
      onSubmit={handleCreateClick}
      instantValidate={false}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <ToggleButtonGroup
          aria-labelledby='violations-view-select'
          value={viewMode}
          exclusive
          size={isPhone ? 'small' : 'medium'}
          color={'primary'}
          sx={{
            backgroundColor: 'secondary.main',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'row',
            height: 60,
            borderBottom: '2px solid black',
          }}
          fullWidth
          onChange={(event, newViewMode) => {
            if (newViewMode) setViewMode(newViewMode);
          }}
        >
          <ToggleButton
            value='active'
            key={'active'}
            sx={{ flex: 1, borderRadius: 0 }}
          >
            <Typography
              variant='body1'
              color={
                viewMode === 'active'
                  ? palette.primary.main
                  : palette.secondary.contrastText
              }
            >
              {t('active')}
            </Typography>
          </ToggleButton>
          <ToggleButton
            value='inactive'
            key='inactive'
            sx={{ flex: 1, borderRadius: 0 }}
          >
            <Typography
              variant='body1'
              color={
                viewMode === 'inactive'
                  ? palette.primary.main
                  : palette.secondary.contrastText
              }
            >
              {t('inactive')}
            </Typography>
          </ToggleButton>
          {allowCreate && (
            <ToggleButton
              value='create'
              key='create'
              sx={{ flex: 1, borderRadius: 0 }}
            >
              <Typography
                variant='body1'
                color={
                  viewMode === 'create'
                    ? palette.primary.main
                    : palette.secondary.contrastText
                }
              >
                {t('create')}
              </Typography>
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Paper>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {viewMode === 'create' ? (
          <FormFrame loading={false}>
            <DetailsHeader
              title={t('buttons.create', { name: t('violation.label', { ns: 'entities' }) })}
              submitLabel={t('buttons.create', { name: t('violation.label', { ns: 'entities' }) })}
              disableSubmit={name === '' || !allowCreate}
              cancelButtonLabel={t('clear')}
              onCancel={handleClearForm}
              onSubmit={handleCreateClick}
            />
            <Grid container width={'100%'} spacing={1.5} mt={1}>
              <Grid size={12}>
                <AuturaTextField
                  name='name'
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                  label={t('violation.name', { ns: 'entities' })}
                  disabled={!allowCreate}
                />
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  name='description'
                  onChange={(e) => setDescription(e.target.value)}
                  value={description}
                  label={t('violation.description', { ns: 'entities' })}
                  disabled={!allowCreate}
                />
              </Grid>
            </Grid>
          </FormFrame>
        ) : (
          <Box
            sx={{
              height: `calc(100dvh - 330px)`,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              pr: 1,
            }}
          >
            <List
              disablePadding
              aria-label={`${
                viewMode === 'active'
                  ? t('violation.activeListHeader')
                  : t('violation.inactiveListHeader')
              }`}
            >
              {violationsList.map((item) => {
                return (
                  <ViolationListItem
                    key={`${item.id}${item.name}${item.description}`}
                    item={item}
                    active={viewMode === 'active'}
                    sx={{ px: { xs: 2, sm: 4 } }}
                    onMoveClick={
                      viewMode === 'active'
                        ? handleExcludeViolation
                        : handleIncludeViolation
                    }
                    onDeleteClick={handleDeleteViolation}
                    canUpdateViolations={allowUpdate}
                  />
                );
              })}
            </List>
          </Box>
        )}
      </Box>
    </ValidatorForm>
  );
};

export default MobileViolations;
